/** @typedef {import('./BalancedNode').BalancedNode} BalancedNode */

/**
 * Search/filter helpers for the balanced editor (HU-02).
 *
 * Matching is by node name, by substring, case- and accent-insensitive (D2).
 * The tree search filters (hides non-matching nodes while keeping each match's
 * ancestor hierarchy); the per-column search highlights matches in place.
 *
 * @module balancedSearch
 */

/**
 * Lowercases a string and strips its diacritics, tolerating null/undefined.
 *
 * @param {string} value
 * @returns {string}
 */
export const normalizeText = (value) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()

/**
 * Whether `name` contains `term` as a case- and accent-insensitive substring.
 * An empty or whitespace-only term never matches.
 *
 * @param {string} name
 * @param {string} term
 * @returns {boolean}
 */
export const matchesTerm = (name, term) => {
  const needle = normalizeText(term).trim()
  if (!needle) return false
  return normalizeText(name).includes(needle)
}

/**
 * Whether `term` matches the node's `code` OR its `name`, each evaluated on its
 * own (story 135, RN7, RN7.2).
 *
 * Deliberately NOT evaluated against the composed `CODE (NAME)` label: the
 * parentheses are presentation still pending the design session (D-a), so a
 * purely visual adjustment would silently change what the user can find. That is
 * why "0100 (INST" and "0100 INSTALACION" do not match a job whose visible label
 * is "0100 (INSTALACION DE ASIENTOS)".
 *
 * A field that is absent is simply not evaluated; it is never matched as if it
 * carried the other field's value (RN7.3). Both the tree search and the station
 * column search call this same primitive, which is what keeps them from drifting
 * apart (risk R7).
 *
 * @param {string} code
 * @param {string} name
 * @param {string} term
 * @returns {boolean}
 */
export const matchesCodeOrName = (code, name, term) =>
  matchesTerm(code, term) || matchesTerm(name, term)
